var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk } from "@reduxjs/toolkit";
import { authApi } from "../../../api/enpoints/auth-api";
import localStorageAccessor from "../../../utility/local-storage-accessor";
import { getUserInfoFromToken } from "../../../utility/jwt-decoder";
export const sendOtp = createAsyncThunk("auth/sendOtp", (email_1, _a) => __awaiter(void 0, [email_1, _a], void 0, function* (email, { rejectWithValue }) {
    var _b, _c;
    try {
        const response = yield authApi.sendOtp({ email });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(((_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) || (error === null || error === void 0 ? void 0 : error.message) || "Failed to send OTP");
    }
}));
export const verifyOtp = createAsyncThunk("auth/verifyOtp", (credentials_1, _a) => __awaiter(void 0, [credentials_1, _a], void 0, function* (credentials, { rejectWithValue }) {
    var _b, _c;
    try {
        const response = yield authApi.verifyOtp(credentials);
        if (response.data.data) {
            const { accessToken, refreshToken, user } = response.data.data;
            localStorageAccessor.setAccessToken(accessToken);
            localStorageAccessor.setRefreshToken(refreshToken);
            // Note: User data will be set by the reducer from API response
            // This ensures API response is the source of truth for permissions
            // JWT token is only used as fallback if API response is missing
        }
        return response.data;
    }
    catch (error) {
        return rejectWithValue(((_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) ||
            (error === null || error === void 0 ? void 0 : error.message) ||
            "Failed to verify OTP");
    }
}));
export const resendOtp = createAsyncThunk("auth/resendOtp", (email_1, _a) => __awaiter(void 0, [email_1, _a], void 0, function* (email, { rejectWithValue }) {
    var _b, _c;
    try {
        const response = yield authApi.resendOtp({ email });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(((_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) ||
            (error === null || error === void 0 ? void 0 : error.message) ||
            "Failed to resend OTP");
    }
}));
export const refreshAccessToken = createAsyncThunk("auth/refreshAccessToken", (credentials_1, _a) => __awaiter(void 0, [credentials_1, _a], void 0, function* (credentials, { rejectWithValue }) {
    var _b, _c;
    try {
        const response = yield authApi.refreshAccessToken(credentials);
        if (response.data.data) {
            const accessToken = response.data.data;
            localStorageAccessor.setAccessToken(accessToken);
            // Extract user info from refreshed JWT token
            // Note: Refresh token endpoint might not return user data in response
            const userInfo = getUserInfoFromToken(accessToken);
            if (userInfo) {
                const roles = Array.isArray(userInfo.role)
                    ? userInfo.role
                    : userInfo.role
                        ? [userInfo.role]
                        : [];
                localStorageAccessor.setUser({
                    id: userInfo.sub,
                    name: `${userInfo.firstName} ${userInfo.lastName}`,
                    email: userInfo.email,
                    userName: userInfo.userName,
                    firstName: userInfo.firstName,
                    lastName: userInfo.lastName,
                    tenantId: userInfo.tenantId,
                    roles: roles,
                    permissions: userInfo.permission || [],
                });
            }
        }
        return response.data;
    }
    catch (error) {
        return rejectWithValue(((_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) ||
            (error === null || error === void 0 ? void 0 : error.message) ||
            "Failed to refresh access token");
    }
}));
export const logoutUser = createAsyncThunk("auth/logoutUser", () => __awaiter(void 0, void 0, void 0, function* () {
    try {
        yield authApi.logout();
    }
    catch (error) {
        console.error("Logout API failed:", error);
    }
    finally {
        localStorageAccessor.removeAccessToken();
        localStorageAccessor.removeRefreshToken();
        localStorageAccessor.removeUser();
    }
    return true;
}));
export const startImpersonation = createAsyncThunk("auth/startImpersonation", (targetUserId_1, _a) => __awaiter(void 0, [targetUserId_1, _a], void 0, function* (targetUserId, { rejectWithValue }) {
    var _b, _c;
    try {
        const { usersApi } = yield import("../../../api/enpoints/users-api");
        const response = yield usersApi.startImpersonation(targetUserId);
        if (response.data.isSuccess && response.data.data) {
            const { accessToken, user } = response.data.data;
            localStorageAccessor.setAccessToken(accessToken);
            localStorageAccessor.setApiUser(user);
            return response.data;
        }
        return rejectWithValue(response.data.message || "Failed to start impersonation");
    }
    catch (error) {
        return rejectWithValue(((_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) ||
            (error === null || error === void 0 ? void 0 : error.message) ||
            "Failed to start impersonation");
    }
}));
// Note: stopImpersonation is handled by the reducer action, not a thunk
// This is just a placeholder for consistency
export const stopImpersonationThunk = createAsyncThunk("auth/stopImpersonation", (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { rejectWithValue }) {
    try {
        // Just return success - the reducer will handle restoring the original user
        return true;
    }
    catch (error) {
        return rejectWithValue((error === null || error === void 0 ? void 0 : error.message) || "Failed to stop impersonation");
    }
}));
